import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const NotificationsContext = createContext({
  items: [], unread: 0, badges: {},
  markAllRead: () => {}, markRead: () => {}, refresh: () => {},
});

export const useNotifications = () => useContext(NotificationsContext);

const POLL_MS = 15000; // same cadence as the dispatch polling

export function NotificationsProvider({ children }) {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], unread: 0, badges: {} });
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const { data: d } = await apiClient.get("/my/notifications");
      setData({ items: d.items || [], unread: d.unread || 0, badges: d.badges || {} });
    } catch (e) { void e; }
  }, []);

  useEffect(() => {
    if (!user) {
      setData({ items: [], unread: 0, badges: {} });
      return undefined;
    }
    refresh();
    timerRef.current = setInterval(refresh, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [user?.user_id, refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const markAllRead = useCallback(async () => {
    setData((d) => ({ ...d, unread: 0, items: d.items.map((n) => ({ ...n, read: true })) }));
    try { await apiClient.post("/my/notifications/mark-all-read"); } catch (e) { void e; }
  }, []);

  const markRead = useCallback(async (id) => {
    setData((d) => ({
      ...d,
      unread: Math.max(0, d.unread - 1),
      items: d.items.map((n) => (n.notification_id === id ? { ...n, read: true } : n)),
    }));
    try { await apiClient.post(`/my/notifications/${id}/read`); } catch (e) { void e; }
  }, []);

  return (
    <NotificationsContext.Provider value={{ ...data, markAllRead, markRead, refresh }}>
      {children}
    </NotificationsContext.Provider>
  );
}
